import React, { useState } from "react";
import { api } from "../../lib/api";
import { X, Phone, AlertTriangle, CheckCircle, MapPin } from "lucide-react";

export default function NeedAssistanceModal({ visit, client, onClose, onSuccess }) {
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [gps, setGps] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);

  const getCurrentLocation = async () => {
    setGpsLoading(true);
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
        });
      });
      setGps({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      });
    } catch (err) {
      console.warn("GPS not available:", err);
      // Continue without GPS - it's optional for assistance
    } finally {
      setGpsLoading(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    // Try to get location first
    if (!gps && navigator.geolocation) {
      await getCurrentLocation();
    }

    try {
      await api.post("/evv/need-assistance", {
        visit_id: visit?.id || null,
        client_id: client?.id || visit?.client_id || null,
        gps_latitude: gps?.latitude || null,
        gps_longitude: gps?.longitude || null,
        note: note.trim() || null,
      });
      
      setSuccess(true);
      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to send alert");
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4">
        <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md p-6 text-center animate-in slide-in-from-bottom duration-300">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Help is on the way!</h3>
          <p className="text-gray-500 mt-2">
            The office has been notified and will contact you immediately.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-in slide-in-from-bottom duration-300">
        {/* Header - Red theme for urgency */}
        <div className="sticky top-0 bg-red-600 text-white px-6 py-4 rounded-t-3xl sm:rounded-t-2xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <Phone className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-semibold">Need Assistance</h2>
              <p className="text-sm text-white/80">Alert the office</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/20 transition-colors"
            data-testid="close-assistance-modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {error && (
            <div className="flex items-start gap-3 p-3 rounded-xl bg-red-50 text-red-700">
              <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Warning message */}
          <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">This will send an urgent alert to the office.</p>
              <p className="mt-1">Use this when you need immediate help or support.</p>
            </div>
          </div>

          {/* Context info */}
          {(visit || client) && (
            <div className="bg-gray-50 rounded-xl p-4 space-y-2">
              {visit && (
                <>
                  <div className="text-sm text-gray-500">Currently at</div>
                  <div className="font-medium text-gray-900">{visit.client_name || client?.first_name + " " + client?.last_name}</div>
                </>
              )}
              {(client?.address || visit?.client?.address) && (
                <div className="flex items-start gap-1 text-sm text-gray-500">
                  <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{client?.address || visit?.client?.address}</span>
                </div>
              )}
            </div>
          )}

          {/* GPS status */}
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <MapPin className="w-4 h-4" />
            {gpsLoading ? (
              <span>Getting your location...</span>
            ) : gps ? (
              <span className="text-emerald-600">Location captured</span>
            ) : (
              <span>Location will be shared when you send</span>
            )}
          </div>

          {/* Note input */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              What do you need help with? (optional)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Describe the situation..."
              rows={4}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-colors resize-none text-sm"
              data-testid="assistance-note-input"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white px-6 py-4 border-t border-gray-100 space-y-3">
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full py-3.5 px-4 rounded-xl bg-red-600 text-white font-medium disabled:opacity-50 hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
            data-testid="submit-assistance-request"
          >
            <Phone className="w-5 h-5" />
            {loading ? "Sending Alert..." : "Send Assistance Request"}
          </button>
          <button
            onClick={onClose}
            className="w-full py-3 px-4 rounded-xl border border-gray-200 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
            data-testid="cancel-assistance"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
